import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { GOAL_CHIP_LAYOUT_ID } from "./motion/tokens";
import { chipStrings, goalCardStrings } from "./strings";
import type { GoalDefinition } from "./goalsConfig";

/**
 * GoalChip — the flow-header chip the selected goal card morphs into
 * (shared layoutId with the selected card in GoalSelectionStep). The compact
 * variant is quiet toolbar metadata: category dot + MRX method tag, no pill.
 */

const CATEGORY_DOT: Record<GoalDefinition["category"], string> = {
  test: "bg-emerald-500",
  brand: "bg-sky-500",
  explore: "bg-amber-500",
};

interface GoalChipProps {
  goal: GoalDefinition;
  compact?: boolean;
  className?: string;
}

export function GoalChip({ goal, compact, className }: GoalChipProps) {
  const shouldReduceMotion = useReducedMotion();
  const methodTag = goalCardStrings[goal.id].methodTag

  if (compact) {
    return (
      <motion.div
        layoutId={shouldReduceMotion ? undefined : GOAL_CHIP_LAYOUT_ID}
        data-testid="goal-chip"
        aria-label={chipStrings.goalAriaLabel}
        className={cn(
          "inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground",
          className,
        )}
      >
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            CATEGORY_DOT[goal.category],
          )}
        />
        {methodTag}
      </motion.div>
    );
  }

  const Icon = goal.icon;

  return (
    <motion.div
      layoutId={shouldReduceMotion ? undefined : GOAL_CHIP_LAYOUT_ID}
      data-testid="goal-chip"
      aria-label={chipStrings.goalAriaLabel}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground",
        className,
      )}
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="whitespace-nowrap leading-none">{methodTag}</span>
    </motion.div>
  );
}
